import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEmployeeInformation,
  addEducation,
  removeEducation,
  updateEducationField,
  setEducationImage,
  removeEducationImage,
  setEducationSkipped,
} from "../../../Redux/employeeInformationSlice.js";
import styles from "./EducationDetails.module.scss";

const EDUCATION_ORDER = ["10th", "12th", "ITI", "Diploma", "UG", "PG", "OTHER"];

const EducationDetails = forwardRef(function EducationDetails(_, ref) {
  const dispatch = useDispatch();

  const { educationDetails, educationImages, educationSkipped } = useSelector(
    selectEmployeeInformation,
  );

  const skipped = educationSkipped;

  const [errorFields, setErrorFields] = useState({});

  const clearError = (id, name) => {
    setErrorFields((prev) => {
      const entryErrors = new Set(prev[id] || []);
      entryErrors.delete(name);
      return { ...prev, [id]: entryErrors };
    });
  };

  const hasError = (id, name) => errorFields[id]?.has(name) ?? false;

  const addSection = (type) => {
    if (!type || type === "Select Education") return;

    const exists = educationDetails.entries.find((e) => e.type === type);
    if (exists) return;

    dispatch(
      addEducation({
        id: Date.now(),
        type,
        school_name: "",
        school_place: "",
        year_of_passing: "",
        course_name: "",
        branch_name: "",
        standard: "",
      }),
    );
  };

  const removeSection = (id) => {
    if (educationImages[id]?.url) {
      URL.revokeObjectURL(educationImages[id].url);
    }

    dispatch(removeEducation(id));

    setErrorFields((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const updateField = (id, name, value) => {
    dispatch(updateEducationField({ id, name, value }));
    clearError(id, name);
  };

  const handleFile = (id, file) => {
    if (!file || file.size > 10 * 1024 * 1024) return;

    if (educationImages[id]?.url) {
      URL.revokeObjectURL(educationImages[id].url);
    }

    dispatch(
      setEducationImage({
        id,
        image: {
          name: file.name,
          file,
          url: URL.createObjectURL(file),
        },
      }),
    );
  };

  const getMissingFields = useCallback(() => {
    if (skipped) return [];

    if (educationDetails.entries.length === 0) {
      return ["At least one education entry"];
    }

    const missing = [];
    const newErrorFields = {};

    educationDetails.entries.forEach((entry) => {
      const entryErrors = new Set();

      if (!String(entry.school_name || "").trim()) {
        missing.push(`${entry.type} Institute Name`);
        entryErrors.add("school_name");
      }

      if (!String(entry.school_place || "").trim()) {
        missing.push(`${entry.type} Place`);
        entryErrors.add("school_place");
      }

      if (!String(entry.year_of_passing || "").trim()) {
        missing.push(`${entry.type} Year of Passing`);
        entryErrors.add("year_of_passing");
      }

      newErrorFields[entry.id] = entryErrors;
    });

    setErrorFields(newErrorFields);

    return missing;
  }, [skipped, educationDetails]);

  useImperativeHandle(
    ref,
    () => ({
      getMissingFields,
      isComplete: () => getMissingFields().length === 0,
    }),
    [getMissingFields],
  );

  const sortedEntries = [...educationDetails.entries].sort(
    (a, b) => EDUCATION_ORDER.indexOf(a.type) - EDUCATION_ORDER.indexOf(b.type),
  );

  const renderFields = (entry) => {
    const is10th12th = entry.type === "10th" || entry.type === "12th";
    const isOther = entry.type === "OTHER";

    return (
      <div key={entry.id} className={styles.section}>
        <div className={styles.sectionHeaderRow}>
          <h3>{entry.type}</h3>
          <button type="button" onClick={() => removeSection(entry.id)}>
            ✕
          </button>
        </div>

        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>
              {entry.type} {is10th12th ? "School Name" : "Institute Name"}
              <b>*</b>
            </span>
            <input
              value={entry.school_name || ""}
              className={
                hasError(entry.id, "school_name") ? styles.inputError : ""
              }
              onChange={(e) =>
                updateField(
                  entry.id,
                  "school_name",
                  e.target.value.toUpperCase(),
                )
              }
            />
            {hasError(entry.id, "school_name") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              {entry.type} Place<b>*</b>
            </span>
            <input
              value={entry.school_place || ""}
              className={
                hasError(entry.id, "school_place") ? styles.inputError : ""
              }
              onChange={(e) =>
                updateField(
                  entry.id,
                  "school_place",
                  e.target.value.toUpperCase(),
                )
              }
            />
            {hasError(entry.id, "school_place") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>

          {!is10th12th && !isOther && (
            <>
              <label className={styles.field}>
                <span>Course</span>
                <input
                  value={entry.course_name || ""}
                  onChange={(e) =>
                    updateField(
                      entry.id,
                      "course_name",
                      e.target.value.toUpperCase(),
                    )
                  }
                />
              </label>

              <label className={styles.field}>
                <span>Branch</span>
                <input
                  value={entry.branch_name || ""}
                  onChange={(e) =>
                    updateField(
                      entry.id,
                      "branch_name",
                      e.target.value.toUpperCase(),
                    )
                  }
                />
              </label>
            </>
          )}

          {isOther && (
            <label className={styles.field}>
              <span>Qualification</span>
              <input
                value={entry.standard || ""}
                onChange={(e) =>
                  updateField(
                    entry.id,
                    "standard",
                    e.target.value.toUpperCase(),
                  )
                }
              />
            </label>
          )}

          <label className={styles.field}>
            <span>
              Year Of Passing<b>*</b>
            </span>
            <input
              value={entry.year_of_passing || ""}
              inputMode="numeric"
              maxLength={4}
              className={
                hasError(entry.id, "year_of_passing") ? styles.inputError : ""
              }
              onChange={(e) =>
                updateField(
                  entry.id,
                  "year_of_passing",
                  e.target.value.replace(/\D/g, "").slice(0, 4),
                )
              }
            />
            {hasError(entry.id, "year_of_passing") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>
        </div>

        <div className={styles.uploadBlock}>
          <span>File</span>

          <p className={styles.note}>
            Note: Upload the file. Verify that the file is in JPG, JPEG, or PNG
            format and that its size is less than 10 MB.
          </p>

          {educationImages[entry.id] ? (
            <div className={styles.previewRow}>
              <span>{educationImages[entry.id].name}</span>
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => dispatch(removeEducationImage(entry.id))}
              >
                Remove
              </button>
            </div>
          ) : (
            <label className={styles.uploadButton}>
              Upload file
              <input
                type="file"
                className={styles.hiddenInput}
                accept="image/png,image/jpeg,image/jpg"
                onChange={(e) => handleFile(entry.id, e.target.files?.[0])}
              />
            </label>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.educationDetails}>
      <header className={styles.sectionHeader}>Education Details</header>

      <div className={styles.content}>
        <div className={styles.skipRow}>
          {skipped ? (
            <button
              type="button"
              className={styles.unskipButton}
              onClick={() => dispatch(setEducationSkipped(false))}
            >
              Add Education Details
            </button>
          ) : (
            <button
              type="button"
              className={styles.skipButton}
              onClick={() => {
                dispatch(setEducationSkipped(true));
                setErrorFields({});
              }}
            >
              Skip Education Details
            </button>
          )}
        </div>

        {skipped ? (
          <p className={styles.skippedNote}>
            Education details skipped. Click "Add Education Details" to fill
            them in.
            <br />
            Click Save and Continue to go to the next section.
          </p>
        ) : (
          <>
            <select
              className={styles.dropdown}
              defaultValue=""
              onChange={(e) => {
                addSection(e.target.value);
                e.target.value = "";
              }}
            >
              <option value="">Select Education</option>
              {EDUCATION_ORDER.map((item) => (
                <option key={item} value={item}>
                  {item.toUpperCase()}
                </option>
              ))}
            </select>

            {sortedEntries.map(renderFields)}
          </>
        )}
      </div>
    </div>
  );
});

export default EducationDetails;
